/**
 * Finding one customer in a 704-account book, and putting a just-pinned one
 * where the eye already is.
 *
 * Both of these exist because of the same discovery: the change list is capped
 * at 25 rows per column, so a customer at position 547 of "Staying" is invisible
 * even though the totals beside it are right. That cap is correct — rendering
 * 704 names in a table cell helps nobody — but it makes two ordinary actions
 * silent:
 *
 *   - PINNING. The manager pins a customer, the counts move by one, and the
 *     customer they just acted on drops below the fold. `pinnedFirst` puts the
 *     newest pin at the top of the list it landed in, so the confirmation is the
 *     row itself rather than a pair of counters.
 *   - LOOKING SOMETHING UP. "Who has Kestrel Institute?" had no answer short of
 *     expanding books one at a time. `matchesAccount` + `displayedAccounts`
 *     narrow the table to the portfolios that hold a match.
 *
 * Pure functions over plain data, deliberately: the ordering rule and the
 * "matched but in no book" rule are both things a spec can pin without a DOM,
 * and the second one is the honesty guard — see `unplacedMatches`.
 */
import type {
  Account,
  Portfolio,
  StaffingResult,
} from '@andavo/staffing-engine';

/**
 * Trim and case-fold. An empty result means NOT SEARCHING — the caller shows
 * the whole table — so whitespace alone must not filter anything away.
 *
 * "Needle", not "query": cm-api has a `normalizeQuery` that clamps URL
 * parameters, and two functions of that name doing unrelated things is the kind
 * of collision that reads fine until someone imports the wrong one.
 */
export function normalizeNeedle(raw: string): string {
  return raw.trim().toLowerCase();
}

/**
 * Name substring, nothing cleverer.
 *
 * Matching the customer id too was tempting and is left out on purpose: ids here
 * are opaque CRM keys, a partial one matches an arbitrary set of unrelated
 * customers, and a manager searching "1042" would get a list they cannot explain.
 * The needle is assumed already normalized.
 */
export function matchesAccount(account: Account, needle: string): boolean {
  return account.name.toLowerCase().includes(needle);
}

/**
 * Every account a portfolio ROW displays, across its three columns.
 *
 * NOT the engine's `portfolioAccounts`, which is a different set for a good
 * reason: that one is the RESULTING book (assigned + pinned), and this one is
 * what the row SHOWS — which includes customers leaving. A manager searching for
 * a customer wants the row that is losing it just as much as the row gaining it,
 * so the two must not be conflated. `assigned` is absent here because the page
 * renders keeping / incoming / losing / otherOut / pinned, and adding the
 * balancer's own view of the same customers would double-count every match.
 */
export function displayedAccounts(portfolio: Portfolio): Account[] {
  return [
    ...portfolio.keeping,
    ...portfolio.pinned,
    ...portfolio.incoming,
    ...portfolio.losing,
    ...Object.values(portfolio.otherOut).flat(),
  ];
}

/** Does this portfolio hold anything matching the needle? */
export function portfolioMatches(
  portfolio: Portfolio,
  needle: string,
): boolean {
  return displayedAccounts(portfolio).some((a) => matchesAccount(a, needle));
}

/**
 * Pinned to THIS book first, newest pin first within that.
 *
 * The sort is stable, so everything unpinned keeps the order the caller chose
 * (the engine's). `pinnedHere` is customerId → the pin's `createdAt`, already
 * narrowed to one CSM, which is what makes "the one I just pinned" — the newest
 * timestamp — land at row one rather than merely somewhere in the pinned group.
 */
export function pinnedFirst(
  accounts: readonly Account[],
  pinnedHere: ReadonlyMap<string, string>,
): Account[] {
  if (pinnedHere.size === 0) return [...accounts];
  return [...accounts].sort((a, b) => {
    const pa = pinnedHere.get(a.id);
    const pb = pinnedHere.get(b.id);
    if (pa === undefined && pb === undefined) return 0;
    if (pa === undefined) return 1;
    if (pb === undefined) return -1;
    // ISO-8601 sorts lexically. Ties (same millisecond, or a fixture that
    // reuses one timestamp) fall back to name so the order is deterministic.
    return pb.localeCompare(pa) || a.name.localeCompare(b.name);
  });
}

export interface UnplacedMatch {
  account: Account;
  /** The tier they fall in, or null if the engine placed them nowhere at all. */
  tierKey: string | null;
}

/**
 * Matched customers that NO portfolio holds.
 *
 * This is the honesty half of search, and it is the whole reason search reads
 * from the full account list rather than from the rendered rows. A tier with no
 * staffed slot keeps its whole book in `result.unallocated`, out of every
 * portfolio — the engine has a named field for exactly that case because an
 * earlier version dropped those accounts silently. If search only walked the
 * portfolios, typing a real customer's name would answer "no match" for a
 * customer that plainly exists, which is the same lie one layer up.
 */
export function unplacedMatches(
  result: StaffingResult,
  matched: readonly Account[],
): UnplacedMatch[] {
  const placed = new Set<string>();
  for (const portfolio of result.portfolios) {
    for (const account of displayedAccounts(portfolio)) placed.add(account.id);
  }

  const tierOf = new Map<string, string>();
  for (const [tierKey, list] of Object.entries(result.unallocated)) {
    for (const account of list) tierOf.set(account.id, tierKey);
  }

  return matched
    .filter((account) => !placed.has(account.id))
    .map((account) => ({ account, tierKey: tierOf.get(account.id) ?? null }));
}
